import type { AxiosError } from "axios";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import React, { useContext, useState } from "react";
import { endpoints } from "../../../../../endpoints";
import type {
  Assets,
  PIMProduct,
  PIMProductStatusAck,
} from "../../../../../types/types.PIM";
import {
  isAxiosError,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../../util/util";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import type { DataMutate, WithPagination } from "../../../../../types/types";
import { EditDeleteModal } from "../../../../../components/EditDeleteModal/EditDeleteModal";

export const RemoveAssetFromProduct = ({
  show,
  onClose,
  onComplete,
  product,
  refreshAssetsList,
  assetData,
  onRemoveAsset,
  confirmationBody,
}: {
  onComplete: (productId?: string) => void;
  product?: PIMProduct;
  refreshAssetsList: DataMutate<WithPagination<{ data: Assets[] }>>;
  show: boolean;
  onClose: () => void;
  assetData: Assets | null;
  onRemoveAsset?: (assetId: string) => Promise<void>;
  confirmationBody?: string;
}) => {
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { notifySuccess, notifyError } = useContext(Notifications);

  const { tenant_id } = useStoreState();

  const { id } = assetData as Assets;

  const updateProductStatus = useUpdateProductStatus({
    product:
      product ??
      ({
        id: "",
        status: "draft",
        product_number: null,
      } as PIMProduct),
  });

  const onDelete = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      let ackedProduct: PIMProductStatusAck | undefined;
      if (onRemoveAsset) {
        await onRemoveAsset(id);
      } else if (product) {
        const { data } = await updateProductStatus();
        ackedProduct = data;
        if (ackedProduct) {
          await Axios.patch(
            endpoints.v2_tenants_id_pim_assets_id(tenant_id, id),
            {
              product_ids_to_delete: [ackedProduct.id],
            }
          );
        }
      } else {
        throw new Error("Missing product for product-asset removal");
      }
      await refreshAssetsList();
      notifySuccess(t("Asset successfully Removed"));
      setIsSubmitting(false);
      onComplete(ackedProduct?.primary_staged_product_id ?? ackedProduct?.id);
    } catch (error) {
      if (
        isAxiosError(error) &&
        (error as AxiosError)?.response?.data?.message
      ) {
        notifyError(error?.response?.data?.message);
      } else {
        notifyError(t("could not remove asset, Something went wrong."));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <EditDeleteModal
        title={t("Remove Asset")}
        show={show}
        onClose={onClose}
        onConfirm={onDelete}
        isLoading={isSubmitting}
        body={
          confirmationBody ??
          t("Are you sure you want to remove this Asset from the product?")
        }
        confirmBtnLabel={t("Remove")}
      />
    </>
  );
};
